import { useQuery } from '@tanstack/react-query'
import { api } from './api'
import { MAX_UPLOAD_BYTES } from './upload-extensions'

/**
 * Effective per-file upload cap in BYTES — the SAME number the server's
 * Multer enforces (the operator-configurable `uploads.max_size_mb`), read
 * from the `/files/storage` summary. Falls back to the hard ceiling
 * (`MAX_UPLOAD_BYTES`) until the summary loads.
 *
 * Why it exists: the client used to pre-check only against the 2 GB ceiling,
 * so a file above a LOWER operator cap passed the gate, streamed all the way
 * up, and Multer aborted it mid-flight. That aborts the request while the
 * browser is still sending the body, which RESETS the connection — so the
 * uploader saw a misleading "Can't reach the server" network error instead
 * of "File is too large". Pre-checking against the real cap rejects the file
 * instantly and never starts the doomed upload.
 *
 * Shared across every upload surface (Files & Links page, a Space's Files
 * tab, task/space attachments) via the common ['files','storage'] cache, so
 * they all enforce one identical number.
 */
export function useMaxUploadBytes() {
  const { data } = useQuery({
    queryKey: ['files', 'storage'],
    queryFn: async () => (await api.get('/files/storage')).data,
    staleTime: 5 * 60 * 1000,
  })
  const bytes = Number(data?.maxUploadBytes)
  return Number.isFinite(bytes) && bytes > 0 ? bytes : MAX_UPLOAD_BYTES
}
